import { X } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@krea/shared/utils";

import { useIsKeyboardOpen } from "../../hooks/useIsKeyboardOpen";
import * as Base from "./base-modal";

interface IHeaderProps {
  title: string;

  hideCloseButton?: boolean;
}

const CloseElement = ({ className }: { className?: string }) => (
  <Base.ResponsiveDialogClose
    className={cn(
      "cursor-pointer text-2xl font-bold text-gray-400 hover:text-gray-600 border-none bg-transparent focus:outline-none focus-visible:outline-none active:outline-none",
      className,
    )}
  >
    <X className="h-4 w-4" strokeWidth={4} />
    <span className="sr-only">Close</span>
  </Base.ResponsiveDialogClose>
);

const Header = ({ title, hideCloseButton }: IHeaderProps) => {
  return (
    <div
      className="flex w-full justify-center rounded-t-xl border-0 border-b border-solid border-gray-200 px-3 py-[5px]"
      id="dialog-header"
    >
      <div className="-mr-4 flex-1 text-center ">
        <h5 className="mx-6 mt-2">{title}</h5>
      </div>

      {!hideCloseButton && <CloseElement />}
    </div>
  );
};

interface IResponsiveDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  children: ReactNode;

  onClose?: () => void;
  title?: string;
  lockOutsideInteraction?: boolean;
  className?: string;
  dialogClassName?: string;
  footer?: ReactNode;
  hideCloseButton?: boolean;
}

export const AppModal = ({
  open,
  setOpen,
  children,
  onClose,
  title,
  lockOutsideInteraction = true,
  className,
  dialogClassName,
  footer,
  hideCloseButton = false,
}: IResponsiveDialogProps) => {
  const isKeyboardOpen = useIsKeyboardOpen(open);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setOpen(nextOpen);
    } else {
      onClose?.();
      setOpen(nextOpen);
    }
  };

  return (
    <Base.ResponsiveDialog open={open} onOpenChange={handleOpenChange}>
      <Base.ResponsiveDialogContent
        className={cn(
          "m-0 py-0  [&_button#-default-dialog-close-icon]:hidden",
          dialogClassName,
        )}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          if (lockOutsideInteraction) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (lockOutsideInteraction) e.preventDefault();
        }}
        id="dialog-content"
      >
        {title && (
          <Base.ResponsiveDialogTitle>
            <Header title={title} hideCloseButton={hideCloseButton} />
          </Base.ResponsiveDialogTitle>
        )}
        <div
          className={cn(
            "w-full pt-0 pb-4",
            "[&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-3xl ",
            "max-h-full",
            {
              "min-h-[200px] max-h-[400px] pb-[30px]": isKeyboardOpen,
            },
            className,
          )}
        >
          {children}
          {footer && (
            <Base.ResponsiveDialogFooter className="mb-6 mt-3 w-full">
              {footer}
            </Base.ResponsiveDialogFooter>
          )}
        </div>
      </Base.ResponsiveDialogContent>
    </Base.ResponsiveDialog>
  );
};
